import { Turbo } from "@hotwired/turbo-rails";
import * as ActiveStorage from "@rails/activestorage";
import {
  showConfirmModal,
  showModal,
  ModalLifecycleEvent,
} from "./overlay/modal";

// Turbo Drive is enabled section-by-section: `Turbo.session.drive` stays off so
// nothing is driven by default, and the two top-level layouts opt their whole
// subtree in via `data-turbo="true"` on <body> (see layouts/base.html.erb and
// platform_admin/admin_base.html.erb). Carve-outs (Avo, which ships its own JS
// bundle and Turbo; cXML PunchOut responses) live outside those layouts and
// are therefore never driven. UJS has been removed — links/forms rely on
// Turbo's native data-turbo-method / data-turbo-confirm attributes.
Turbo.session.drive = false;

// When a form inside a <dialog> submits successfully via Turbo, close the
// dialog before the response renders. Without this, the dialog stays open
// across the body-swap/morph; its top-layer backdrop covers the new page
// and intercepts clicks (Selenium reports <html> as the click target). The
// successful redirect's flash + content render normally once the body
// arrives.
document.addEventListener("turbo:submit-end", ((event: CustomEvent) => {
  const form = event.target as HTMLFormElement | null;
  const dialog = form?.closest("dialog") as HTMLDialogElement | null;
  if (dialog?.open && event.detail?.success) {
    dialog.close();
  }
}) as EventListener);

// Turbo's confirm hook: same modal, Promise-based per Turbo's API. Triggered
// by `data-turbo-confirm` on links/forms inside Drive-enabled regions.
// `Turbo.config` is a real runtime property; @hotwired/turbo ships no .d.ts.
const turboConfirm = (
  message: string,
  element: HTMLFormElement | HTMLAnchorElement,
  submitter: HTMLElement | undefined,
) => {
  const target = (submitter ?? element) as HTMLElement;
  const confirmLabel = target.getAttribute("data-confirm-yes") || "OK";
  const cancelLabel = target.getAttribute("data-confirm-no") || "Cancel";
  return new Promise<boolean>((resolve) => {
    showConfirmModal({
      defaultReason: "cancel",
      negativeButtonReason: "cancel",
      negativeButtonLabel: cancelLabel,
      positiveButtonReason: "confirm",
      positiveButtonLabel: confirmLabel,
      message: message,
      onClosing: ({ detail: { closeReason } }: ModalLifecycleEvent) => {
        resolve(closeReason === "confirm");
      },
    });
  });
};
(
  Turbo as unknown as { config: { forms: { confirm: typeof turboConfirm } } }
).config.forms.confirm = turboConfirm;

// Decor components (bulk-actions bar, switch) request confirmation by
// dispatching a cancelable window event carrying a `resolve` callback, and
// fall back to native `window.confirm()` if nothing handles the event. Bridge
// those to the shared Decor confirm modal so they match the rest of the app.
// Calling preventDefault() signals the dispatcher to skip its native fallback;
// this listener then owns resolving the promise when the modal closes.
const bridgeDecorConfirm = (event: Event) => {
  const detail =
    (
      event as CustomEvent<{
        message?: string;
        title?: string;
        confirmLabel?: string;
        cancelLabel?: string;
        resolve?: (confirmed: boolean) => void;
      }>
    ).detail ?? {};
  if (typeof detail.resolve !== "function") return;
  event.preventDefault();
  showConfirmModal({
    title: detail.title ?? "Are you sure?",
    message: detail.message,
    defaultReason: "cancel",
    negativeButtonReason: "cancel",
    negativeButtonLabel: detail.cancelLabel ?? "Cancel",
    positiveButtonReason: "confirm",
    positiveButtonLabel: detail.confirmLabel ?? "OK",
    onClosing: ({ detail: { closeReason } }: ModalLifecycleEvent) =>
      detail.resolve?.(closeReason === "confirm"),
  });
};
window.addEventListener("decor:bulk-actions:show-confirm", bridgeDecorConfirm);
window.addEventListener("decor:switch:confirm", bridgeDecorConfirm);

// Bulk-action "modal" buttons (e.g. Add Tags / Assign) dispatch this event to
// open a shared placeholder modal and lazy-load their form via content_href.
// Without an app listener the dispatcher only calls the bare dialog's
// showModal() with no content fetch, so the modal opens empty. Bridge it to
// the app modal helper which performs the content_href fetch.
window.addEventListener("decor:bulk-actions:show-modal", ((
  event: CustomEvent,
) => {
  const detail = event.detail ?? {};
  const id = detail.id;
  const href = detail.content_href ?? detail.contentHref;
  if (!id || !href) return;
  event.preventDefault();
  showModal({ id, content_href: href });
}) as EventListener);

console.debug("[Boot] Prepare Rails client side libraries");
// Start Active Storage (direct uploads). UJS is no longer loaded.
ActiveStorage.start();
